import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ThemeProvider,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import legacyTheme from '../../theme';
import { tokens } from '../../theme-next';

/**
 * Dernier verrou avant l'émission des factures bi-mensuelles.
 *
 * Le 2026-08-03, une facture de 25 117,92 € est partie chez une agence en
 * contenant 49 cartes grises appartenant à une autre : deux dossiers avaient
 * reçu le même identifiant interne, et toutes les jointures de facturation
 * passent par cet identifiant. L'erreur a été découverte douze jours plus tard,
 * par le mail de l'agence. Une facture envoyée ne se rattrape pas, elle se
 * négocie — d'où ce point d'arrêt entre le clic et l'appel à Qonto.
 *
 * Les anomalies structurelles bloquent l'émission. Les signaux statistiques
 * (volume inhabituel) sont franchissables : un rattrapage de retard les
 * déclenche légitimement, et personne ne doit être empêché de facturer par une
 * heuristique. Mais ils exigent une action délibérée.
 */

export type PreflightSeverity = 'blocking' | 'warning';

export interface PreflightIssue {
  code: string;
  severity: PreflightSeverity;
  title: string;
  detail: string;
  recommendation: string;
  clientId: string | null;
  agencyName: string | null;
  items: string[];
}

export interface PreflightReport {
  issues: PreflightIssue[];
  blockingCount: number;
  warningCount: number;
  canGenerate: boolean;
}

interface InvoicePreflightDialogProps {
  open: boolean;
  /** null tant que la vérification est en cours. */
  report: PreflightReport | null;
  isLoading: boolean;
  /** Renseigné si la vérification elle-même a échoué : on refuse alors d'émettre. */
  errorMessage?: string | null;
  /** Ce que l'utilisateur s'apprête à faire, pour lever toute ambiguïté sur la portée. */
  actionLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
}

const SEVERITY_VISUALS: Record<PreflightSeverity, { accent: string; surface: string; border: string; label: string }> =
  {
    blocking: {
      accent: tokens.color.red,
      surface: tokens.color.redLight,
      border: tokens.color.redBorder,
      label: 'Bloquant',
    },
    warning: {
      accent: tokens.color.orange,
      surface: tokens.color.orangeLight,
      border: tokens.color.orangeBorder,
      label: 'À vérifier',
    },
  };

const IssueCard = ({ issue }: { issue: PreflightIssue }) => {
  const visual = SEVERITY_VISUALS[issue.severity];
  const Icon = issue.severity === 'blocking' ? ErrorOutlineIcon : WarningAmberIcon;

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '14px',
        padding: '16px 18px',
        marginBottom: '12px',
        backgroundColor: visual.surface,
        border: `1px solid ${visual.border}`,
        borderLeft: `3px solid ${visual.accent}`,
        borderRadius: `${tokens.radius.lg}px`,
      }}
    >
      <Icon sx={{ color: visual.accent, fontSize: 22, marginTop: '1px', flexShrink: 0 }} />

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '6px' }}>
          <Typography sx={{ fontSize: '15px', fontWeight: 700, color: tokens.color.dark, lineHeight: 1.3 }}>
            {issue.title}
          </Typography>
          <Box
            sx={{
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              padding: '2px 8px',
              borderRadius: `${tokens.radius.full}px`,
              color: issue.severity === 'blocking' ? '#FFFFFF' : visual.accent,
              backgroundColor: issue.severity === 'blocking' ? visual.accent : 'transparent',
              border: `1px solid ${visual.accent}`,
            }}
          >
            {visual.label}
          </Box>
        </Box>

        {issue.agencyName && (
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: tokens.color.textSecondary, marginBottom: '4px' }}>
            {issue.agencyName}
          </Typography>
        )}

        <Typography sx={{ fontSize: '14px', color: tokens.color.dark, lineHeight: 1.5, marginBottom: '8px' }}>
          {issue.detail}
        </Typography>

        {issue.items.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
            {issue.items.map((item) => (
              <Box
                key={item}
                sx={{
                  fontSize: '12px',
                  fontWeight: 600,
                  fontFamily: 'monospace',
                  padding: '3px 8px',
                  borderRadius: `${tokens.radius.lg}px`,
                  backgroundColor: '#FFFFFF',
                  border: `1px solid ${visual.border}`,
                  color: tokens.color.dark,
                }}
              >
                {item}
              </Box>
            ))}
          </Box>
        )}

        <Typography sx={{ fontSize: '13px', color: tokens.color.textSecondary, lineHeight: 1.5 }}>
          <Box component="span" sx={{ fontWeight: 700, color: visual.accent }}>
            À faire :{' '}
          </Box>
          {issue.recommendation}
        </Typography>
      </Box>
    </Box>
  );
};

const InvoicePreflightDialog = ({
  open,
  report,
  isLoading,
  errorMessage,
  actionLabel,
  onCancel,
  onConfirm,
}: InvoicePreflightDialogProps) => {
  // L'accusé de réception d'un avertissement ne doit jamais survivre à la
  // fermeture : chaque ouverture repart d'une décision explicite.
  const [acknowledged, setAcknowledged] = useState(false);
  useEffect(() => {
    if (open) setAcknowledged(false);
  }, [open]);

  const hasBlocking = (report?.blockingCount ?? 0) > 0;
  const hasWarning = (report?.warningCount ?? 0) > 0;
  const isClean = !isLoading && !errorMessage && report != null && report.issues.length === 0;
  // Une vérification en échec vaut refus : on ne facture pas sans avoir pu contrôler.
  const canProceed = !isLoading && !errorMessage && !hasBlocking && (!hasWarning || acknowledged);

  const headline = (() => {
    if (isLoading) return 'Vérification en cours…';
    if (errorMessage) return 'Vérification impossible';
    if (hasBlocking) return report!.blockingCount > 1 ? `${report!.blockingCount} anomalies bloquantes` : '1 anomalie bloquante';
    if (hasWarning) return report!.warningCount > 1 ? `${report!.warningCount} points à vérifier` : '1 point à vérifier';
    return 'Aucune anomalie détectée';
  })();

  const subhead = (() => {
    if (isLoading) return 'Contrôle de cohérence des factures sur le point d’être émises.';
    if (errorMessage) return errorMessage;
    if (hasBlocking) return 'Ces factures sont incohérentes. Les émettre facturerait des montants faux à des agences.';
    if (hasWarning) return 'Rien ne bloque, mais ces points méritent un coup d’œil avant l’envoi.';
    return 'Les contrôles de cohérence sont au vert.';
  })();

  const accent = errorMessage || hasBlocking ? tokens.color.red : hasWarning ? tokens.color.orange : tokens.color.green;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: `${tokens.radius['3xl']}px`, boxShadow: '0 24px 64px rgba(0,0,0,0.18)' },
      }}
    >
      <DialogTitle sx={{ padding: '24px 28px 12px' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          {isLoading ? (
            <CircularProgress size={22} thickness={5} sx={{ color: accent, marginTop: '3px' }} />
          ) : isClean ? (
            <CheckCircleRoundedIcon sx={{ color: accent, fontSize: 26 }} />
          ) : (
            <ErrorOutlineIcon sx={{ color: accent, fontSize: 26 }} />
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="span"
              sx={{ display: 'block', fontSize: '22px', fontWeight: 800, letterSpacing: '-0.01em', color: tokens.color.dark }}
            >
              {headline}
            </Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: tokens.color.textSecondary, marginTop: '4px' }}>
              {subhead}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ padding: '8px 28px 20px' }}>
        {!isLoading && !errorMessage && report && report.issues.length > 0 && (
          <Box sx={{ maxHeight: '48vh', overflowY: 'auto', paddingRight: '4px' }}>
            {report.issues.map((issue, index) => (
              <IssueCard key={`${issue.code}-${issue.clientId ?? 'na'}-${index}`} issue={issue} />
            ))}
          </Box>
        )}

        {isClean && (
          <Box
            sx={{
              padding: '16px 18px',
              backgroundColor: tokens.color.greenLight,
              border: `1px solid ${tokens.color.greenBorder}`,
              borderRadius: `${tokens.radius.lg}px`,
            }}
          >
            <Typography sx={{ fontSize: '14px', color: tokens.color.dark, lineHeight: 1.5 }}>
              Aucun dossier partagé entre deux agences, aucun véhicule facturé en double, aucun récépissé rattaché à
              plusieurs dossiers, et des volumes conformes aux habitudes de chaque client.
            </Typography>
          </Box>
        )}

        {/* Acquittement explicite : un avertissement franchi doit être un geste conscient. */}
        {!isLoading && !errorMessage && !hasBlocking && hasWarning && (
          <Box
            component="label"
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              marginTop: '4px',
              padding: '14px 16px',
              backgroundColor: tokens.color.surface,
              border: `1px solid ${tokens.color.border}`,
              borderRadius: `${tokens.radius.lg}px`,
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(event) => setAcknowledged(event.target.checked)}
              style={{ marginTop: '2px', width: 16, height: 16, cursor: 'pointer', accentColor: tokens.color.orange }}
            />
            <Typography sx={{ fontSize: '14px', color: tokens.color.dark, lineHeight: 1.5 }}>
              J’ai vérifié ces points et je confirme que ces factures peuvent être émises.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <ThemeProvider theme={legacyTheme}>
        <DialogActions sx={{ padding: '8px 28px 24px', gap: '12px' }}>
          <Button className="secondaryDealActionButton" onClick={onCancel} sx={{ flex: 1 }}>
            {hasBlocking ? 'Fermer' : 'Annuler'}
          </Button>
          {!hasBlocking && (
            <Button
              className="dealActionButton"
              onClick={onConfirm}
              disabled={!canProceed}
              color="primary"
              variant="contained"
              autoFocus
              endIcon={<ArrowForwardRoundedIcon />}
              sx={{ flex: 2 }}
            >
              {actionLabel}
            </Button>
          )}
        </DialogActions>
      </ThemeProvider>
    </Dialog>
  );
};

export default InvoicePreflightDialog;
